import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Drawer,
  Dropdown,
  Empty,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  theme
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileOutlined,
  FolderAddOutlined,
  FolderOutlined,
  InboxOutlined,
  MailOutlined,
  MenuOutlined,
  MoreOutlined,
  PaperClipOutlined,
  PlusOutlined,
  ReloadOutlined,
  RobotOutlined,
  SendOutlined,
  SettingOutlined,
  StarFilled,
  StarOutlined,
  StopOutlined
} from "@ant-design/icons";
import { FaReply } from "react-icons/fa";
import { MdOutlineArchive, MdOutlineMarkEmailRead, MdOutlineMarkEmailUnread } from "react-icons/md";
import { Error, Success } from "../../../commons/Messages/Messages";
import { mailApi, getErrorMessage, startMailOAuth } from "./mailApi";
import MailAccountModal from "./MailAccountModal";
import ComposeModal from "./ComposeModal";
import MessageView from "./MessageView";
import { displayName, formatAddressList, formatListDate } from "./mailUtils";
import { useMailTranslation } from "./useMailTranslation";
import { readStoredAccount, storeAccount } from "./mailStorage";
import { SocketContext } from "../../../context/SocketContext";
import { useMailAssistant, MAX_BATCH } from "./assistant/useMailAssistant";
import AssistantConsentModal from "./assistant/AssistantConsentModal";
import {
  CATEGORY_LABELS,
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  PRIORITY_RANK
} from "./assistant/assistantLabels";
import { useIsCompact } from "../../../design-system/useMediaQuery";
import PageToolbar from "../../../design-system/PageToolbar";
import "./Mail.css";

const PAGE_SIZE = 30;
const POLL_INTERVAL = 60 * 1000;
// With live IMAP IDLE updates the poll is only a safety net (and covers other folders).
const LIVE_POLL_INTERVAL = 5 * 60 * 1000;

const SPECIAL_FOLDERS = {
  "\\Inbox": { icon: <InboxOutlined />, label: "Inbox" },
  "\\Drafts": { icon: <FileOutlined />, label: "Drafts" },
  "\\Sent": { icon: <SendOutlined />, label: "Sent" },
  "\\Archive": { icon: <MdOutlineArchive />, label: "Archive" },
  "\\Junk": { icon: <StopOutlined />, label: "Spam" },
  "\\Trash": { icon: <DeleteOutlined />, label: "Trash" },
  "\\All": { icon: <MailOutlined />, label: "All mail" },
  "\\Flagged": { icon: <StarOutlined />, label: "Starred" }
};

const Mail = () => {
  const { t, i18n } = useMailTranslation();
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountId, setAccountId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [folder, setFolder] = useState("INBOX");
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [listLoading, setListLoading] = useState(false);
  const [selectedUid, setSelectedUid] = useState(null);
  const [checked, setChecked] = useState([]);
  const [compose, setCompose] = useState({ open: false });
  const [accountModal, setAccountModal] = useState({ open: false, account: null });
  // Phones and portrait tablets show one pane at a time: list -> message, folders in a drawer.
  const isMobile = useIsCompact();
  const [foldersOpen, setFoldersOpen] = useState(false);
  const { token } = theme.useToken();
  const [live, setLive] = useState(false);
  const socket = useContext(SocketContext);
  const [assistantOrder, setAssistantOrder] = useState("date");
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [assistantConsentOpen, setAssistantConsentOpen] = useState(false);
  const [sorting, setSorting] = useState(false);
  const knownAddresses = useRef(new Map());
  const listRequest = useRef(0);

  const account = accounts.find(item => item._id === accountId) || null;
  const assistant = useMailAssistant({
    accountId,
    folder,
    messages,
    language: (i18n.language || "de").slice(0, 2)
  });
  const currentFolder = folders.find(item => item.path === folder);
  const isDraftsFolder = currentFolder?.specialUse === "\\Drafts";
  const showRecipients = ["\\Sent", "\\Drafts"].includes(currentFolder?.specialUse);

  const folderLabel = item =>
    SPECIAL_FOLDERS[item?.specialUse]
      ? t(SPECIAL_FOLDERS[item.specialUse].label)
      : item?.name || "";

  const loadAccounts = useCallback(async (preferId = null) => {
    setAccountsLoading(true);
    try {
      const res = await mailApi.getAccounts();
      const list = res.data || [];
      setAccounts(list);
      const stored = preferId || readStoredAccount();
      const next =
        list.find(item => item._id === stored) || list.find(item => item.isDefault) || list[0];
      switchAccount(next?._id || null);
    } catch (error) {
      Error(getErrorMessage(error, t("Failed to load mail accounts")));
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const loadFolders = useCallback(
    async ({ silent = false } = {}) => {
      if (!accountId) return;
      if (!silent) setFoldersLoading(true);
      try {
        const res = await mailApi.getFolders(accountId);
        setFolders(res.data || []);
      } catch (error) {
        if (!silent) Error(getErrorMessage(error, t("Failed to load folders")));
        // An expired OAuth sign-in is recorded on the account; refresh it to show the banner.
        mailApi
          .getAccounts()
          .then(res => setAccounts(res.data || []))
          .catch(() => {});
      } finally {
        if (!silent) setFoldersLoading(false);
      }
    },
    [accountId]
  );

  const loadMessages = useCallback(
    async ({ silent = false } = {}) => {
      if (!accountId) return;
      const requestId = ++listRequest.current;
      if (!silent) setListLoading(true);
      try {
        const res = await mailApi.getMessages(accountId, {
          folder,
          page,
          pageSize: PAGE_SIZE,
          search: search || undefined,
          filter: filter === "all" ? undefined : filter
        });
        // Ignore responses that arrive after the user switched folder/page.
        if (requestId !== listRequest.current) return;
        const list = res.data?.messages || [];
        setMessages(list);
        setTotal(res.data?.total || 0);
        list.forEach(msg =>
          [...msg.from, ...msg.to].forEach(address => {
            if (address.address) knownAddresses.current.set(address.address.toLowerCase(), address);
          })
        );
      } catch (error) {
        if (!silent && requestId === listRequest.current)
          Error(getErrorMessage(error, t("Failed to load messages")));
      } finally {
        if (!silent && requestId === listRequest.current) setListLoading(false);
      }
    },
    [accountId, folder, page, search, filter]
  );

  const switchAccount = id => {
    setCategoryFilter(null);
    setAccountId(id);
    setFolder("INBOX");
    setPage(1);
    setSelectedUid(null);
  };

  useEffect(() => {
    loadFolders();
    if (accountId) storeAccount(accountId);
  }, [accountId]);

  useEffect(() => {
    setChecked([]);
    loadMessages();
  }, [loadMessages]);

  const refreshQuietly = useRef(() => {});
  refreshQuietly.current = async () => {
    if (accountId) {
      try {
        await mailApi.syncMailbox(accountId, { folder });
      } catch (err) {
        // silent sync fallback
      }
    }
    loadFolders({ silent: true });
    if (page === 1) loadMessages({ silent: true });
  };

  // The server keeps an IMAP IDLE connection open for the INBOX and pushes
  // "mail:changed" over socket.io, so new mail shows up without waiting for a poll.
  useEffect(() => {
    if (!socket || !accountId) return undefined;
    const forThisAccount = handler => event => event?.accountId === accountId && handler(event);
    const watch = () =>
      socket.emit("mail:watch", { token: localStorage.getItem("token"), accountId });
    const handlers = {
      connect: watch,
      disconnect: () => setLive(false),
      "mail:watching": forThisAccount(() => setLive(true)),
      "mail:watch-error": forThisAccount(() => setLive(false)),
      "mail:changed": forThisAccount(() => refreshQuietly.current()),
      "mail:account-removed": forThisAccount(() => loadAccounts())
    };
    Object.entries(handlers).forEach(([event, handler]) => socket.on(event, handler));
    if (socket.connected) watch();
    return () => {
      Object.entries(handlers).forEach(([event, handler]) => socket.off(event, handler));
      socket.emit("mail:unwatch", { accountId });
      setLive(false);
    };
  }, [socket, accountId]);

  // Fallback polling while the tab is visible; much slower once live updates work.
  useEffect(() => {
    if (!accountId) return undefined;
    const timer = setInterval(
      () => document.visibilityState === "visible" && refreshQuietly.current(),
      live ? LIVE_POLL_INTERVAL : POLL_INTERVAL
    );
    return () => clearInterval(timer);
  }, [accountId, live]);

  const changeFolder = path => {
    setFoldersOpen(false);
    setCategoryFilter(null);
    setFolder(path);
    setPage(1);
    setSelectedUid(null);
  };

  const refresh = async () => {
    if (accountId) {
      try {
        await mailApi.syncMailbox(accountId, { folder });
      } catch (err) {
        console.warn("Mail sync warning:", err?.message);
      }
    }
    loadFolders();
    loadMessages();
  };

  const adjustUnseen = (path, delta) =>
    setFolders(prev =>
      prev.map(item =>
        item.path === path ? { ...item, unseen: Math.max(0, item.unseen + delta) } : item
      )
    );

  const applyLocalFlags = (uids, patch) => {
    const set = new Set(uids);
    if (patch.seen !== undefined) {
      const changed = messages.filter(msg => set.has(msg.uid) && msg.seen !== patch.seen).length;
      if (changed) adjustUnseen(folder, patch.seen ? -changed : changed);
    }
    setMessages(prev => prev.map(msg => (set.has(msg.uid) ? { ...msg, ...patch } : msg)));
  };

  const removeLocal = uids => {
    const set = new Set(uids);
    setMessages(prev => prev.filter(msg => !set.has(msg.uid)));
    setTotal(prev => Math.max(0, prev - uids.length));
    setChecked(prev => prev.filter(uid => !set.has(uid)));
    if (set.has(selectedUid)) setSelectedUid(null);
    loadFolders({ silent: true });
  };

  const bulkFlags = async (flag, value, patch) => {
    try {
      await mailApi.updateFlags(accountId, {
        folder,
        uids: checked,
        add: value ? [flag] : [],
        remove: value ? [] : [flag]
      });
      applyLocalFlags(checked, patch);
    } catch (error) {
      Error(getErrorMessage(error, t("Failed to update messages")));
    }
  };

  const bulkMove = async destination => {
    try {
      await mailApi.moveMessages(accountId, { folder, uids: checked, destination });
      removeLocal(checked);
    } catch (error) {
      Error(getErrorMessage(error, t("Failed to move messages")));
    }
  };

  const bulkDelete = async () => {
    try {
      await mailApi.deleteMessages(accountId, { folder, uids: checked });
      removeLocal(checked);
    } catch (error) {
      Error(getErrorMessage(error, t("Failed to delete messages")));
    }
  };

  const toggleStar = async (event, msg) => {
    event.stopPropagation();
    try {
      await mailApi.updateFlags(accountId, {
        folder,
        uids: [msg.uid],
        add: msg.flagged ? [] : ["\\Flagged"],
        remove: msg.flagged ? ["\\Flagged"] : []
      });
      applyLocalFlags([msg.uid], { flagged: !msg.flagged });
    } catch (error) {
      Error(getErrorMessage(error, t("Failed to update message")));
    }
  };

  const promptFolderName = ({ title, initialValue = "", onSubmit }) => {
    let value = initialValue;
    Modal.confirm({
      title,
      icon: <FolderOutlined />,
      content: (
        <Input defaultValue={initialValue} autoFocus onChange={e => (value = e.target.value)} />
      ),
      okText: t("Save"),
      cancelText: t("Cancel"),
      onOk: async () => {
        if (!value.trim()) {
          Error(t("Folder name is required"));
          return Promise.reject();
        }
        await onSubmit(value.trim());
      }
    });
  };

  const handleCreateFolder = () =>
    promptFolderName({
      title: t("New folder"),
      onSubmit: async name => {
        try {
          await mailApi.createFolder(accountId, name);
          Success(t("Folder created"));
          loadFolders();
        } catch (error) {
          Error(getErrorMessage(error, t("Failed to create folder")));
        }
      }
    });

  const handleRenameFolder = item =>
    promptFolderName({
      title: t("Rename folder"),
      initialValue: item.path,
      onSubmit: async newPath => {
        try {
          await mailApi.renameFolder(accountId, item.path, newPath);
          if (folder === item.path) changeFolder(newPath);
          loadFolders();
        } catch (error) {
          Error(getErrorMessage(error, t("Failed to rename folder")));
        }
      }
    });

  const handleDeleteFolder = item =>
    Modal.confirm({
      title: t('Delete folder "{{name}}"?', { name: item.path }),
      icon: <ExclamationCircleOutlined />,
      content: t("All messages in this folder will be permanently deleted from the mail server."),
      okText: t("Delete"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await mailApi.deleteFolder(accountId, item.path);
          if (folder === item.path) changeFolder("INBOX");
          loadFolders();
        } catch (error) {
          Error(getErrorMessage(error, t("Failed to delete folder")));
        }
      }
    });

  const openCompose = (mode = "new", message = null, extra = {}) => {
    setFoldersOpen(false);
    setCompose({ open: true, mode, message, folder, ...extra });
  };

  // Sorts the current page with the assistant: everything not analysed yet, newest first.
  const sortWithAssistant = async ({ consented = false } = {}) => {
    if (!consented && !assistant.status?.consentAt) {
      setAssistantConsentOpen(true);
      return;
    }
    const todo = messages.filter(msg => !assistant.analysisOf(msg));
    if (!todo.length) {
      setAssistantOrder("priority");
      return;
    }
    setSorting(true);
    try {
      const queued = await assistant.analyze(todo.slice(0, MAX_BATCH));
      setAssistantOrder("priority");
      if (queued) Success(t("The assistant is sorting {{count}} emails", { count: queued }));
    } catch (error) {
      Error(getErrorMessage(error, t("Failed to start the analysis")));
    } finally {
      setSorting(false);
    }
  };

  const shownMessages = useMemo(() => {
    let list = messages;
    if (categoryFilter) {
      list = list.filter(msg => assistant.analysisOf(msg)?.category === categoryFilter);
    }
    if (assistantOrder === "priority") {
      const rank = msg => {
        const analysis = assistant.analysisOf(msg);
        return analysis?.status === "done" ? (PRIORITY_RANK[analysis.priority] ?? 4) : 5;
      };
      // Stable: within one priority the newest stays first.
      list = list
        .map((msg, position) => ({ msg, position }))
        .sort((a, b) => rank(a.msg) - rank(b.msg) || a.position - b.position)
        .map(({ msg }) => msg);
    }
    return list;
  }, [messages, assistant.analyses, assistantOrder, categoryFilter]);

  const categoriesOnPage = useMemo(() => {
    const counts = {};
    messages.forEach(msg => {
      const category = assistant.analysisOf(msg)?.category;
      if (category) counts[category] = (counts[category] || 0) + 1;
    });
    return counts;
  }, [messages, assistant.analyses]);

  const handleComposeDone = () => {
    setCompose({ open: false });
    loadFolders({ silent: true });
    loadMessages({ silent: true });
  };

  const handleAccountSaved = saved => {
    setAccountModal({ open: false, account: null });
    loadAccounts(saved?._id);
  };

  const handleAccountDeleted = () => {
    setAccountModal({ open: false, account: null });
    loadAccounts();
  };

  const moveTargets = useMemo(
    () =>
      folders
        .filter(item => item.selectable && item.path !== folder)
        .map(item => ({
          key: item.path,
          label: folderLabel(item) === item.name ? item.path : folderLabel(item)
        })),
    [folders, folder]
  );

  const allChecked = messages.length > 0 && checked.length === messages.length;

  if (accountsLoading) {
    return (
      <div className="mail-center">
        <Spin size="large" />
      </div>
    );
  }

  const accountModalElement = (
    <MailAccountModal
      open={accountModal.open}
      account={accountModal.account}
      onClose={() => setAccountModal({ open: false, account: null })}
      onSaved={handleAccountSaved}
      onDeleted={handleAccountDeleted}
    />
  );

  if (!accounts.length) {
    return (
      <div className="mail-center">
        <Empty
          image={<MailOutlined style={{ fontSize: 64, color: token.colorPrimary }} />}
          description={
            <div>
              <h4>{t("Connect your mailbox")}</h4>
              <p className="mail-muted">
                {t(
                  "Read, send and organise your business email without leaving the platform. Works with any provider that supports IMAP."
                )}
              </p>
            </div>
          }
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAccountModal({ open: true, account: null })}
          >
            {t("Connect mailbox")}
          </Button>
        </Empty>
        {accountModalElement}
      </div>
    );
  }

  const depthOf = item => (item.delimiter ? item.path.split(item.delimiter).length - 1 : 0);

  const accountSelect = (
    <Select
      value={accountId}
      onChange={switchAccount}
      className="mail-account-select"
      aria-label={t("Mailbox")}
      options={accounts.map(item => ({
        value: item._id,
        label: item.displayName ? `${item.displayName} <${item.email}>` : item.email
      }))}
    />
  );

  const folderPane = (
    <div className="mail-folders">
      {isMobile && accountSelect}
      <Button
        type="primary"
        block
        icon={<EditOutlined />}
        onClick={() => openCompose()}
        className="mail-folders-compose"
      >
        {t("Compose")}
      </Button>
      <Spin spinning={foldersLoading}>
        <ul className="mail-folder-list">
          {folders.map(item => {
            const special = SPECIAL_FOLDERS[item.specialUse];
            const editable = !item.specialUse && item.selectable;
            return (
              <li
                key={item.path}
                className={`mail-folder ${item.path === folder ? "active" : ""} ${item.selectable ? "" : "disabled"}`}
                style={{ paddingInlineStart: 10 + depthOf(item) * 14 }}
                onClick={() => item.selectable && changeFolder(item.path)}
              >
                <span className="mail-folder-icon">{special?.icon || <FolderOutlined />}</span>
                <span className="mail-folder-name">{folderLabel(item)}</span>
                {item.unseen > 0 && item.specialUse !== "\\Sent" && (
                  <Badge
                    count={item.unseen}
                    overflowCount={999}
                    color={token.colorPrimary}
                    size="small"
                  />
                )}
                {editable && (
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        { key: "rename", label: t("Rename") },
                        { key: "delete", label: t("Delete"), danger: true }
                      ],
                      onClick: ({ key, domEvent }) => {
                        domEvent.stopPropagation();
                        if (key === "rename") handleRenameFolder(item);
                        else handleDeleteFolder(item);
                      }
                    }}
                  >
                    <Button
                      type="text"
                      size="small"
                      className="mail-folder-more"
                      icon={<MoreOutlined />}
                      aria-label={t("More actions")}
                      onClick={e => e.stopPropagation()}
                    />
                  </Dropdown>
                )}
              </li>
            );
          })}
        </ul>
      </Spin>
      <Button
        type="text"
        block
        icon={<FolderAddOutlined />}
        onClick={handleCreateFolder}
        className="mail-folder-add"
      >
        {t("New folder")}
      </Button>
    </div>
  );

  const btnSize = isMobile ? "middle" : "small";

  const listPane = (
    <div className="mail-list">
      <div className="mail-list-toolbar">
        <Input.Search
          allowClear
          type="search"
          enterKeyHint="search"
          placeholder={t("Search subject, sender or recipient")}
          onSearch={value => {
            setSearch(value.trim());
            setPage(1);
          }}
        />
        <Segmented
          size={btnSize}
          block={isMobile}
          value={filter}
          onChange={value => {
            setFilter(value);
            setPage(1);
          }}
          options={[
            { value: "all", label: t("All") },
            { value: "unread", label: t("Unread") },
            { value: "flagged", label: t("Starred") }
          ]}
        />
      </div>
      {assistant.status?.configured && (
        <div className="mail-list-assistant">
          <Button
            size={btnSize}
            icon={<RobotOutlined />}
            loading={sorting}
            onClick={() => sortWithAssistant()}
          >
            {t("Sort with AI")}
          </Button>
          {Object.keys(categoriesOnPage).length > 0 && (
            <>
              <Segmented
                size={btnSize}
                value={assistantOrder}
                onChange={setAssistantOrder}
                options={[
                  { value: "date", label: t("Newest") },
                  { value: "priority", label: t("Priority") }
                ]}
              />
              <Select
                size={btnSize}
                allowClear
                value={categoryFilter}
                onChange={value => setCategoryFilter(value || null)}
                placeholder={t("Category")}
                className="mail-category-select"
                options={Object.entries(categoriesOnPage).map(([category, count]) => ({
                  value: category,
                  label: `${t(CATEGORY_LABELS[category] || category)} (${count})`
                }))}
              />
            </>
          )}
        </div>
      )}
      <div className="mail-list-actions">
        <span className="mail-check">
          <Checkbox
            aria-label={t("Select all")}
            checked={allChecked}
            indeterminate={checked.length > 0 && !allChecked}
            onChange={e => setChecked(e.target.checked ? messages.map(msg => msg.uid) : [])}
          />
        </span>
        {checked.length > 0 ? (
          <Space size={2} wrap>
            <Tooltip title={t("Mark as read")}>
              <Button
                type="text"
                size={btnSize}
                aria-label={t("Mark as read")}
                icon={<MdOutlineMarkEmailRead />}
                onClick={() => bulkFlags("\\Seen", true, { seen: true })}
              />
            </Tooltip>
            <Tooltip title={t("Mark as unread")}>
              <Button
                type="text"
                size={btnSize}
                aria-label={t("Mark as unread")}
                icon={<MdOutlineMarkEmailUnread />}
                onClick={() => bulkFlags("\\Seen", false, { seen: false })}
              />
            </Tooltip>
            <Tooltip title={t("Star")}>
              <Button
                type="text"
                size={btnSize}
                aria-label={t("Star")}
                icon={<StarOutlined />}
                onClick={() => bulkFlags("\\Flagged", true, { flagged: true })}
              />
            </Tooltip>
            <Dropdown
              menu={{ items: moveTargets, onClick: ({ key }) => bulkMove(key) }}
              trigger={["click"]}
            >
              <Button type="text" size={btnSize} icon={<FolderOutlined />}>
                <span className="mail-btn-label">{t("Move to")}</span>
              </Button>
            </Dropdown>
            <Popconfirm
              title={t("Delete {{count}} messages?", { count: checked.length })}
              onConfirm={bulkDelete}
              okText={t("Delete")}
              cancelText={t("Cancel")}
            >
              <Button
                type="text"
                size={btnSize}
                danger
                aria-label={t("Delete")}
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
            <span className="mail-muted">{t("{{count}} selected", { count: checked.length })}</span>
          </Space>
        ) : (
          <Space size={2}>
            <Tooltip title={t("Refresh")}>
              <Button
                type="text"
                size={btnSize}
                aria-label={t("Refresh")}
                icon={<ReloadOutlined />}
                onClick={refresh}
              />
            </Tooltip>
            <Tooltip
              title={live ? t("Live updates are on") : t("Checking for new mail every minute")}
            >
              <span className={`mail-live-dot ${live ? "on" : ""}`} />
            </Tooltip>
            <span className="mail-muted">{folderLabel(currentFolder) || folder}</span>
          </Space>
        )}
      </div>

      <Spin spinning={listLoading} wrapperClassName="mail-list-spin">
        {shownMessages.length === 0 && !listLoading ? (
          <Empty
            className="mt-5"
            description={
              search || filter !== "all" || categoryFilter
                ? t("No matching messages")
                : t("This folder is empty")
            }
          />
        ) : (
          <ul className="mail-items">
            {shownMessages.map(msg => {
              const analysis = assistant.analysisOf(msg);
              const people = showRecipients
                ? `${t("To")}: ${msg.to.map(displayName).join(", ") || "—"}`
                : msg.from.map(displayName).join(", ") || t("(unknown sender)");
              return (
                <li
                  key={msg.uid}
                  className={`mail-item ${msg.seen ? "" : "unread"} ${selectedUid === msg.uid ? "active" : ""}`}
                  onClick={() => setSelectedUid(msg.uid)}
                >
                  {/* The wrapper stops row clicks, so its whole touch area only toggles the box. */}
                  <span className="mail-check" onClick={e => e.stopPropagation()}>
                    <Checkbox
                      aria-label={t("Select message")}
                      checked={checked.includes(msg.uid)}
                      onChange={e =>
                        setChecked(prev =>
                          e.target.checked
                            ? [...prev, msg.uid]
                            : prev.filter(uid => uid !== msg.uid)
                        )
                      }
                    />
                  </span>
                  <button
                    type="button"
                    className={`mail-item-star ${msg.flagged ? "on" : ""}`}
                    aria-label={msg.flagged ? t("Remove star") : t("Star")}
                    aria-pressed={msg.flagged}
                    onClick={e => toggleStar(e, msg)}
                  >
                    {msg.flagged ? <StarFilled /> : <StarOutlined />}
                  </button>
                  <div className="mail-item-body">
                    <div className="mail-item-top">
                      <span
                        className="mail-item-from"
                        title={formatAddressList(showRecipients ? msg.to : msg.from)}
                      >
                        {people}
                      </span>
                      <span className="mail-item-date">{formatListDate(msg.date)}</span>
                    </div>
                    <div className="mail-item-subject">
                      {msg.answered && <FaReply className="mail-item-answered" />}
                      {msg.subject || t("(no subject)")}
                      {msg.hasAttachments && <PaperClipOutlined className="mail-item-clip" />}
                    </div>
                    {analysis?.status === "processing" && (
                      <div className="mail-item-assistant">
                        <Spin size="small" /> <span className="mail-muted">{t("Sorting…")}</span>
                      </div>
                    )}
                    {analysis?.status === "done" && (
                      <div className="mail-item-assistant">
                        <Tag color={PRIORITY_COLORS[analysis.priority]} bordered={false}>
                          {t(PRIORITY_LABELS[analysis.priority])}
                        </Tag>
                        <Tag bordered={false}>{t(CATEGORY_LABELS[analysis.category])}</Tag>
                        {analysis.phishingWarning && (
                          <Tag color="red" bordered={false}>
                            {t("Suspicious")}
                          </Tag>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Spin>
      {total > PAGE_SIZE && (
        <Pagination
          className="mail-pagination"
          size={isMobile ? "default" : "small"}
          simple
          current={page}
          pageSize={PAGE_SIZE}
          total={total}
          onChange={setPage}
        />
      )}
    </div>
  );

  const readerPane = (
    <MessageView
      account={account}
      folder={folder}
      uid={selectedUid}
      folders={folders}
      isDraftsFolder={isDraftsFolder}
      onCompose={openCompose}
      onFlagsChanged={applyLocalFlags}
      onRemoved={removeLocal}
      onBack={isMobile ? () => setSelectedUid(null) : null}
      assistant={assistant.status?.configured ? assistant : null}
    />
  );

  const settingsButton = (
    <Tooltip title={t("Mailbox settings")}>
      <Button
        icon={<SettingOutlined />}
        aria-label={t("Mailbox settings")}
        onClick={() => setAccountModal({ open: true, account })}
      />
    </Tooltip>
  );

  const reading = isMobile && Boolean(selectedUid);
  const currentUnseen = currentFolder?.specialUse !== "\\Sent" ? currentFolder?.unseen : 0;

  return (
    <div
      className={`mail-page${isMobile ? " mail-page--compact" : ""}`}
      style={{ "--mail-success": token.colorSuccess, "--mail-star": token.colorWarning }}
    >
      {isMobile ? (
        // One compact row; the reader has its own toolbar with a back button.
        !reading && (
          <div className="mail-mobilebar">
            <Button
              className="mail-mobilebar-folder"
              icon={<MenuOutlined />}
              onClick={() => setFoldersOpen(true)}
              aria-label={t("Folders")}
              aria-expanded={foldersOpen}
            >
              <span className="mail-mobilebar-folder-name">
                {folderLabel(currentFolder) || folder}
              </span>
              {currentUnseen > 0 && (
                <Badge count={currentUnseen} overflowCount={999} color={token.colorPrimary} />
              )}
            </Button>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => openCompose()}
              aria-label={t("Compose")}
            >
              <span className="mail-btn-label">{t("Compose")}</span>
            </Button>
            {settingsButton}
            <Tooltip title={t("Add mailbox")}>
              <Button
                icon={<PlusOutlined />}
                aria-label={t("Add mailbox")}
                onClick={() => setAccountModal({ open: true, account: null })}
              />
            </Tooltip>
          </div>
        )
      ) : (
        <PageToolbar
          className="mail-header"
          filters={accountSelect}
          actions={
            <>
              {settingsButton}
              <Button
                icon={<PlusOutlined />}
                onClick={() => setAccountModal({ open: true, account: null })}
              >
                {t("Add mailbox")}
              </Button>
            </>
          }
        />
      )}

      {account?.authType === "oauth2" && account.lastError && (
        <Alert
          type="warning"
          showIcon
          message={t(account.lastError)}
          action={
            <Button
              size="small"
              type="primary"
              onClick={() =>
                startMailOAuth(account.oauth?.provider, account.email).catch(error =>
                  Error(getErrorMessage(error, t("Failed to start sign-in")))
                )
              }
            >
              {t("Reconnect")}
            </Button>
          }
        />
      )}
      <div className="mail-panes">
        {!isMobile && folderPane}
        {!reading && listPane}
        {(!isMobile || selectedUid) && <div className="mail-reader-pane">{readerPane}</div>}
      </div>

      {isMobile && (
        <Drawer
          open={foldersOpen}
          onClose={() => setFoldersOpen(false)}
          placement={i18n.dir?.() === "rtl" ? "right" : "left"}
          width={300}
          title={t("Folders")}
          rootClassName="mail-folders-drawer"
        >
          {folderPane}
        </Drawer>
      )}

      <ComposeModal
        open={compose.open}
        context={compose}
        account={account}
        knownAddresses={Array.from(knownAddresses.current.values())}
        onClose={() => setCompose({ open: false })}
        onDone={handleComposeDone}
      />
      {accountModalElement}
      <AssistantConsentModal
        open={assistantConsentOpen}
        email={account?.email}
        onCancel={() => setAssistantConsentOpen(false)}
        onConfirm={async () => {
          await assistant.setConsent(true);
          setAssistantConsentOpen(false);
          // The button that asked for consent was "Sort with AI", so carry on with that.
          sortWithAssistant({ consented: true });
        }}
      />
    </div>
  );
};

export default Mail;
